/*
 * First-order offer hive (sections/hive-promo.liquid).
 * Brings the hive in once the page has loaded and shakes it (a bee buzzes out) as it arrives,
 * then at random intervals until it's opened (for the rest of that visit), and runs the offer, which opens in
 * the middle of the screen over a dimmed, locked page: copy the code, apply it to the cart,
 * close, or hide the hive for 30 days. Once the code is on the cart the hive goes away: it
 * leaves when the offer closes, stays away for 30 days, and never shows while the cart already
 * carries the code. A browser flagged as a past buyer (the hive_ordered cookie from the
 * checkout pixel, or the flag a signed-in buyer's visit leaves) never sees it.
 */
(() => {
  const STATE_KEY = 'hivePromo'; // localStorage: { hideUntil } after "No thanks"
  const OPENED_KEY = 'hivePromoOpened'; // sessionStorage: opened during this visit
  const HIDE_DAYS = 30;

  const readState = () => {
    try {
      return JSON.parse(localStorage.getItem(STATE_KEY)) || {};
    } catch (e) {
      return {};
    }
  };
  const writeState = (patch) => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify({ ...readState(), ...patch }));
    } catch (e) {}
  };
  const isPastBuyer = () => {
    if (/(?:^|;\s*)hive_ordered=1(?:;|$)/.test(document.cookie)) return true;
    try {
      return localStorage.getItem('hive_ordered') === '1';
    } catch (e) {
      return false;
    }
  };
  // Shopify's discount link leaves the code in a discount_code cookie until checkout.
  const hasCodeApplied = (code) => {
    const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (!match || !code) return false;
    let value = match[1];
    try {
      value = decodeURIComponent(value);
    } catch (e) {}
    return value.split(',').some((applied) => applied.trim().toUpperCase() === code.toUpperCase());
  };
  const random = (min, max) => min + Math.random() * (max - min);
  const openedThisVisit = () => {
    try {
      return sessionStorage.getItem(OPENED_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  class HivePromo extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      const state = readState();
      this.preview = this.hasAttribute('data-preview');
      this.code = this.dataset.code;
      const hiddenForNow = state.hideUntil && Date.now() < state.hideUntil;
      if (!this.preview && (isPastBuyer() || hiddenForNow || hasCodeApplied(this.code))) {
        this.remove();
        return;
      }

      this.hive = this.querySelector('.hpromo__hive');
      this.panel = this.querySelector('.hpromo__panel');
      this.backdrop = this.querySelector('.hpromo__backdrop');
      this.status = this.querySelector('.hpromo__status');
      // Opening it only calms the hive for the rest of the visit; the next visit it shakes again.
      this.opened = openedThisVisit();
      if (this.opened) this.classList.add('is-opened');
      if ('opened' in state) writeState({ opened: undefined }); // the first version kept this for good
      this.calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.hive.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close(true)));
      // One shake runs every animation on the hive; the bee's flight is the longest, so its end
      // marks the end of the shake.
      this.querySelector('.hpromo__flight').addEventListener('animationend', (event) => {
        if (event.animationName === 'hpromo-fly') this.hive.classList.remove('is-shaking');
      });
      this.querySelectorAll('[data-hpromo-close]').forEach((el) => el.addEventListener('click', () => this.close(true)));
      this.querySelector('[data-hpromo-copy]').addEventListener('click', (event) => this.copy(event.currentTarget));
      this.querySelector('[data-hpromo-apply]').addEventListener('click', (event) => this.apply(event.currentTarget));
      this.querySelector('[data-hpromo-dismiss]').addEventListener('click', () => this.dismiss());
      this.onKey = (event) => {
        if (event.key === 'Escape') this.close(true);
      };

      // After the page has finished loading, so the hive never competes with the page itself:
      // its two images start downloading then, and the hive rises in a moment later.
      const images = this.querySelectorAll('img[loading="lazy"]');
      const reveal = () => {
        images.forEach((img) => (img.loading = 'eager'));
        this.hidden = false;
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-in')));
        // the first shake lands as the hive finishes rising in; shake() then sets the next one
        if (!this.opened) this.schedule(500);
      };
      const afterLoad = () => {
        images.forEach((img) => (img.loading = 'eager'));
        setTimeout(reveal, 1200);
      };
      if (this.preview) reveal();
      else if (document.readyState === 'complete') afterLoad();
      else window.addEventListener('load', afterLoad, { once: true });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.close(false);
    }

    schedule(delay) {
      clearTimeout(this.timer);
      if (this.calm || this.opened) return;
      this.timer = setTimeout(() => this.shake(), delay);
    }

    // Skipped while the tab is in the background or something covers the page (search, menu).
    shake() {
      const body = document.body;
      const busy =
        document.hidden ||
        !this.panel.hidden ||
        document.documentElement.classList.contains('hive-overlay-open') ||
        body.classList.contains('overflow-hidden') ||
        body.classList.contains('overflow-hidden-tablet') ||
        body.classList.contains('overflow-hidden-desktop');
      if (!busy) this.hive.classList.add('is-shaking');
      this.schedule(random(8000, 20000));
    }

    open() {
      clearTimeout(this.timer);
      this.hive.classList.remove('is-shaking');
      // Lock the page the way the search panel does: the scrollbar's width stays as padding so
      // nothing underneath shifts. Measured before anything changes.
      const gutter = window.innerWidth - document.documentElement.clientWidth;
      if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
      document.body.classList.add('overflow-hidden');
      this.setOverlay(true);
      this.backdrop.hidden = false;
      this.panel.hidden = false;
      this.hive.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      if (!this.opened) {
        this.opened = true;
        this.classList.add('is-opened');
        if (!this.preview) {
          try {
            sessionStorage.setItem(OPENED_KEY, '1');
          } catch (e) {}
        }
      }
      // Keyboard focus stays inside the offer while it's open (trapFocus is in global.js).
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
      else this.panel.focus({ preventScroll: true });
      document.addEventListener('keydown', this.onKey);
    }

    close(returnFocus) {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.backdrop.hidden = true;
      this.hive.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.body.style.removeProperty('padding-right');
      this.setOverlay(false);
      document.removeEventListener('keydown', this.onKey);
      // Focus only goes back to the hive if it's staying.
      const refocus = returnFocus && !this.applied;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(refocus ? this.hive : null);
      else if (refocus) this.hive.focus({ preventScroll: true });
      if (this.applied) this.leave();
    }

    // Same signal the search panel sends, so the hero slideshow and card glints pause while
    // they're covered.
    setOverlay(open) {
      const root = document.documentElement;
      if (root.classList.contains('hive-overlay-open') === open) return;
      root.classList.toggle('hive-overlay-open', open);
      document.dispatchEvent(new CustomEvent('hive:overlay', { detail: { open } }));
    }

    say(message) {
      this.status.textContent = message;
    }

    async copy(button) {
      try {
        await navigator.clipboard.writeText(this.code);
      } catch (e) {
        // Older browsers: select the code so a long-press or Ctrl+C copies it.
        const range = document.createRange();
        range.selectNodeContents(this.querySelector('.hpromo__code-value'));
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
      button.textContent = 'Copied';
      this.say(`${this.code} copied. Paste it at checkout.`);
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => (button.textContent = 'Copy'), 2000);
    }

    // Shopify's discount link saves the code for this visitor, and checkout applies it.
    async apply(button) {
      button.disabled = true;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(
          `${root}discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent(`${root}cart.js`)}`,
          { credentials: 'same-origin' }
        );
        if (!response.ok) throw new Error(response.status);
        button.classList.add('is-done');
        button.querySelector('span').textContent = 'Applied to your cart';
        this.say('Done. Your discount comes off at checkout.');
        // The job's done: the hive leaves when the offer closes and stays away.
        this.applied = true;
        this.querySelector('[data-hpromo-dismiss]').hidden = true;
        if (!this.preview) writeState({ hideUntil: Date.now() + HIDE_DAYS * 864e5 });
      } catch (e) {
        button.disabled = false;
        this.say(`That didn't go through. Enter ${this.code} at checkout instead.`);
      }
    }

    dismiss() {
      if (!this.preview) writeState({ hideUntil: Date.now() + HIDE_DAYS * 864e5 });
      this.close(false);
      this.leave();
    }

    // The hive slides back down out of sight, then it's taken off the page.
    leave() {
      clearTimeout(this.timer);
      this.classList.remove('is-in');
      setTimeout(() => this.remove(), 450);
    }
  }

  customElements.define('hive-promo', HivePromo);
})();
